import React, { useEffect, useState } from 'react';
import { UserCog } from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import {
  assignmentService,
  agencyStaffPickerService,
  type AssignmentPayload,
  type AgencyStaffOption,
  type MyWorkRow,
} from '../../services/auditService';

/**
 * Plan 05a-03 / STAFF-03 — Assignment Modal.
 *
 * Opened by the AuditorDashboard when a Program Head clicks "Assign" on a
 * procedure-instance row. Fields: Performer (responsible_user_id), Approver
 * (approver_user_id), Due date. Validates performer != approver. Submits via
 * PUT /adt/procedure_instances/{id} (assignmentService.update).
 *
 * Reassignment is allowed at ANY workflow_state per Decision 3, but a
 * warning banner appears when state has progressed past Draft.
 */

export interface AssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The procedure-instance row being assigned. Caller passes the full MyWorkRow so the modal can show context (program name, current state) without a refetch. */
  row: MyWorkRow | null;
  /** Agency id used to scope the staff picker. Caller resolves this from the row's parent program. */
  agencyId: number | null;
  /** USR_users.id of the logged-in user — sent as modified_by on the PUT. */
  currentUserId: number;
  /** Called after the PUT succeeds so the parent can refetch /adt/my-work. */
  onSaved: () => void;
}

const AssignmentModal: React.FC<AssignmentModalProps> = ({
  isOpen,
  onClose,
  row,
  agencyId,
  currentUserId,
  onSaved,
}) => {
  const [performerId, setPerformerId] = useState<number | null>(null);
  const [approverId, setApproverId]   = useState<number | null>(null);
  const [dueDate, setDueDate]         = useState<string>('');
  const [staff, setStaff]             = useState<AgencyStaffOption[]>([]);
  const [loadingStaff, setLoadingStaff] = useState<boolean>(false);
  const [submitting, setSubmitting]   = useState<boolean>(false);
  const [error, setError]             = useState<string | null>(null);

  // Seed local state when the modal opens with a row.
  useEffect(() => {
    if (!isOpen || !row) return;
    setPerformerId(row.responsible_user_id ?? null);
    setApproverId(row.approver_user_id ?? null);
    setDueDate(row.due_date ?? '');
    setError(null);
  }, [isOpen, row]);

  // Load the staff picker when modal opens (or when the agency changes).
  useEffect(() => {
    if (!isOpen || !agencyId) return;
    let cancelled = false;
    setLoadingStaff(true);
    agencyStaffPickerService
      .list(agencyId)
      .then((options) => { if (!cancelled) { setStaff(options); setLoadingStaff(false); } })
      .catch((err) => {
        if (cancelled) return;
        setStaff([]);
        setLoadingStaff(false);
        const msg = err?.response?.data?.detail ?? err?.message ?? 'Could not load agency staff.';
        setError(typeof msg === 'string' ? msg : 'Could not load agency staff.');
      });
    return () => { cancelled = true; };
  }, [isOpen, agencyId]);

  if (!row) return null;

  const performerEqualsApprover =
    performerId !== null && approverId !== null && performerId === approverId;

  // C26 v4 — a performer may also be the approver. The pairing is warned, never
  // blocked: single-person engagements are routine and the assigner decides.
  const canSubmit =
    !submitting &&
    !loadingStaff &&
    performerId !== null &&
    approverId !== null;

  const handleSubmit = async () => {
    if (!canSubmit || !row) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload: AssignmentPayload = {
        responsible_user_id: performerId,
        approver_user_id: approverId,
        due_date: dueDate || null,
        modified_by: currentUserId,
      };
      await assignmentService.update(row.id, payload);
      setSubmitting(false);
      onSaved();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? err?.message ?? 'Failed to save assignment.';
      setError(typeof msg === 'string' ? msg : 'Failed to save assignment.');
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" closeOnEscape closeOnOverlayClick data-testid="MOD_assignment_modal">
      <Modal.Header>
        <span className="modal-header-icon-row">
          <UserCog size={16} />
          Assign Procedure
        </span>
      </Modal.Header>

      <Modal.Body>
        <div className="observation-context-card">
          <div className="observation-context-title">
            {row.procedure_name ?? row.label ?? 'Procedure'}
          </div>
          <div className="observation-context-row">
            {row.program_name ?? '—'}
            {row.procedure_type ? ` · ${row.procedure_type}` : ''}
            {' · '}
            workflow: {row.workflow_state}
          </div>
        </div>

        <div className="assign-form-grid">
          <FormControl.Select
            label="Performer"
            value={performerId ?? ''}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setPerformerId(e.target.value ? Number(e.target.value) : null)
            }
            disabled={loadingStaff}
            data-testid="SEL_assignment_performer"
          >
            <option value="">{loadingStaff ? 'Loading staff…' : 'Select performer…'}</option>
            {staff.map((s) => (
              <option key={`p-${s.user_id}`} value={s.user_id}>
                {s.full_name}
              </option>
            ))}
          </FormControl.Select>

          <FormControl.Select
            label="Approver"
            value={approverId ?? ''}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setApproverId(e.target.value ? Number(e.target.value) : null)
            }
            disabled={loadingStaff}
            data-testid="SEL_assignment_approver"
          >
            <option value="">{loadingStaff ? 'Loading staff…' : 'Select approver…'}</option>
            {staff.map((s) => (
              <option key={`a-${s.user_id}`} value={s.user_id}>
                {s.full_name}
              </option>
            ))}
          </FormControl.Select>
        </div>

        <div className="assign-form-block">
          <FormControl.DatePicker
            label="Due date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            data-testid="INP_assignment_due_date"
          />
        </div>

        {performerEqualsApprover && (
          <div className="warning-banner" role="status"
               data-testid="WRN_assignment_same_performer_approver">
            Performer and approver are the same person. This work will be performed and
            approved by one individual — permitted, and recorded as self-reviewed.
          </div>
        )}

        {error && (
          <div className="inline-error inline-error--inline" role="alert">
            {error}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary-outline" onClick={onClose} disabled={submitting} data-testid="BTN_assignment_cancel">
          Cancel
        </Button>
        <Button variant="fill" onClick={handleSubmit} disabled={!canSubmit} data-testid="BTN_assignment_submit">
          {submitting ? 'Assigning…' : 'Save assignment'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AssignmentModal;
